// Series primitive that paints translucent red/green vertical bands over the
// main chart pane based on PowerTrend active/inactive ranges. Active periods
// are tinted green, gaps between (and before/after) are tinted red. Colours
// match those used for plot bands on the /market_breadth chart and blend over
// both light and dark backgrounds via low alpha.
//
// Pattern follows the lightweight-charts v5 plugin example
// CrosshairHighlightPrimitive: a new renderer is returned per renderer() call,
// updateAllViews() pushes fresh data into pane views, and requestUpdate() is
// invoked on attach to trigger the first paint.

export const ACTIVE_BG = 'rgba(104, 187, 138, 0.25)'   // green
export const INACTIVE_BG = 'rgba(218, 104, 104, 0.25)' // red

// Fallback when timeScale.options().barSpacing is unavailable. Matches the
// lightweight-charts internal default so visuals look right even without
// explicit barSpacing configuration.
const DEFAULT_BAR_SPACING = 6

// Binary search: lowest index i where data[i].time >= dateStr.
// Returns data.length when every element is strictly less than dateStr.
// Assumes data is sorted ascending by .time (ISO date strings).
export function findIndexAtOrAfter(data, dateStr) {
  let lo = 0
  let hi = data.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (data[mid].time < dateStr) lo = mid + 1
    else hi = mid - 1
  }
  return lo
}

// Binary search: highest index i where data[i].time <= dateStr.
// Returns -1 when every element is strictly greater than dateStr.
// Assumes data is sorted ascending by .time (ISO date strings).
export function findIndexAtOrBefore(data, dateStr) {
  let lo = 0
  let hi = data.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (data[mid].time > dateStr) hi = mid - 1
    else lo = mid + 1
  }
  return hi
}

// Convert PowerTrend period objects into bar-index ranges aligned to the
// chart's data array. Returns an array of [startIdx, endIdx] tuples sorted
// by startIdx. Periods without a start_date are skipped; periods whose
// start_date falls after the last bar (or end_date before the first bar)
// are dropped because they have no on-chart representation.
export function buildActiveRanges(periods, data) {
  const ranges = []
  for (const period of periods) {
    if (!period.start_date) continue

    const s = findIndexAtOrAfter(data, period.start_date)
    const e = period.end_date
      ? findIndexAtOrBefore(data, period.end_date)
      : data.length - 1

    if (s > e || s >= data.length || e < 0) continue
    ranges.push([ s, e ])
  }
  return ranges.sort((a, b) => a[0] - b[0])
}

class PowerTrendBackgroundRenderer {
  constructor(data) {
    this._data = data
  }

  draw(target) {
    const { chart, periods, data } = this._data
    if (!chart || !data || data.length === 0) return

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const h = scope.bitmapSize.height
      const w = scope.bitmapSize.width
      const pr = scope.horizontalPixelRatio

      // logicalToCoordinate only accepts integer logicals — fractional inputs
      // silently return 0 — so we look up bar centres and extend by half a
      // barSpacing on each side to span each candle's full column.
      const barSpacing = timeScale.options().barSpacing || DEFAULT_BAR_SPACING
      const halfBar = barSpacing / 2

      const xOf = (idx, offset) => {
        const centre = timeScale.logicalToCoordinate(idx)
        return centre === null ? null : (centre + offset) * pr
      }

      // Paint a band between two bar indices. `leftOpen` / `rightOpen` extend
      // the band past the bar edge to the pane edge — used for the inactive
      // tails that bound the data on either side.
      const paintBand = (color, fromIdx, toIdx, { leftOpen = false, rightOpen = false } = {}) => {
        const x1 = leftOpen ? 0 : xOf(fromIdx, -halfBar)
        const x2 = rightOpen ? w : xOf(toIdx, halfBar)
        if (x1 === null || x2 === null) return
        ctx.fillStyle = color
        ctx.fillRect(x1, 0, x2 - x1, h)
      }

      const activeRanges = buildActiveRanges(periods, data)
      const lastIdx = data.length - 1

      // No active periods: the entire data range is inactive.
      if (activeRanges.length === 0) {
        paintBand(INACTIVE_BG, 0, lastIdx, { leftOpen: true, rightOpen: true })
        return
      }

      // Inactive tail before the first active range.
      const firstStart = activeRanges[0][0]
      if (firstStart > 0) {
        paintBand(INACTIVE_BG, 0, firstStart - 1, { leftOpen: true })
      }

      // Each active range, plus the inactive gap that follows it.
      for (let i = 0; i < activeRanges.length; i++) {
        const [ s, e ] = activeRanges[i]
        paintBand(ACTIVE_BG, s, e)

        const next = activeRanges[i + 1]
        if (next) {
          if (next[0] > e + 1) paintBand(INACTIVE_BG, e + 1, next[0] - 1)
        } else if (e < lastIdx) {
          paintBand(INACTIVE_BG, e + 1, lastIdx, { rightOpen: true })
        }
      }
    })
  }
}

class PowerTrendBackgroundPaneView {
  constructor(data) {
    this._data = data
  }

  update(data) {
    this._data = data
  }

  renderer() {
    return new PowerTrendBackgroundRenderer(this._data)
  }

  zOrder() {
    return 'bottom'
  }
}

export class PowerTrendBackground {
  constructor({ periods, data }) {
    this._sharedData = {
      chart: null,
      periods: periods || [],
      data: data || []
    }
    this._paneViews = [new PowerTrendBackgroundPaneView(this._sharedData)]
    this._attachedParams = null
  }

  attached(param) {
    this._attachedParams = param
    this._sharedData.chart = param.chart
    this.updateAllViews()
    param.requestUpdate()
  }

  detached() {
    this._sharedData.chart = null
    this._attachedParams = null
  }

  updateAllViews() {
    this._paneViews.forEach(pv => pv.update(this._sharedData))
  }

  paneViews() {
    return this._paneViews
  }
}
